import { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Fab, styled, SxProps, Theme } from '@mui/material';
import DashboardNavbar from './Navbar';
import LoadingScreen from '~/components/LoadingScreen';
import { useAuthState } from 'ra-core';
import { Redirect } from 'react-router';
import { useSelector } from 'react-redux';
import { getReadingModeState } from '~/reducers/readingMode';
import { useSaveLocationBookmark } from '~/authProvider/locationBookmark';


interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayoutRoot = styled('div')(
  ({ theme }) => (
    {
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      width: '100%'
    }
  )
);

const DashboardLayoutWrapper = styled('div')(
  ({ theme }) => (
    {
      display: 'flex',
      flex: '1 1 auto',
      overflow: 'hidden',
      paddingTop: '64px',
      // [theme.breakpoints.up('lg')]: {
      //   paddingLeft: '280px'
      // }
    }
  )
);

const DashboardLayoutContainer = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden'
});

const DashboardLayoutContent = styled('div')({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  WebkitOverflowScrolling: 'touch'
});

const DashboardLayout: FC<DashboardLayoutProps> = ({ children }) => {
  const [, setIsSidebarMobileOpen] = useState<boolean>(false);
  useEffect(() => {
    let recaptchaBadge = document.querySelector('.grecaptcha-badge')
    if (recaptchaBadge) {
      recaptchaBadge.remove()
      recaptchaBadge = null
    }
  }, [])
  const saveBookmark = useSaveLocationBookmark()

  const overflowContainerRef = useRef<HTMLDivElement>(null)
  const backToTopFn = useCallback(() => overflowContainerRef.current?.scrollTo(0, 0), [])
  const readingMode = useSelector<any, boolean>(getReadingModeState)
  const { loading, authenticated } = useAuthState()
  if (loading) {
    return (
      <LoadingScreen />
    )
  }

  if (!authenticated) {
    saveBookmark()
    return (
      <Redirect to="/login" />
    )
  }

  return (
    <DashboardLayoutRoot sx={{ height: readingMode ? '100%' : '100vh' }}>
      <DashboardNavbar
        onSidebarMobileOpen={(): void => setIsSidebarMobileOpen(true)}
      />
      <DashboardLayoutWrapper>
        <DashboardLayoutContainer>
          <DashboardLayoutContent className="scroll-host" ref={overflowContainerRef}>
            <ScrollTopContext.Provider value={backToTopFn}>
              {children}
            </ScrollTopContext.Provider>
          </DashboardLayoutContent>
        </DashboardLayoutContainer>
      </DashboardLayoutWrapper>
    </DashboardLayoutRoot>
  );
};

export default DashboardLayout;


interface BackToTopFn {
  (): void;
}

const ScrollTopContext = createContext<BackToTopFn>(()=>{})

export const BackToTopButton : FC<{sx?: SxProps<Theme> | undefined}> = ({sx}) => {
  const backToTop = useContext(ScrollTopContext)
  return (
    <Fab color="secondary" aria-label="back to top" size="small" 
      sx={{ position: 'fixed', bottom: 56, right: 56, opacity: 0.2, 
        '&:hover': { opacity: 1 },
        ...sx,
      }}
      onClick={backToTop}
    >
      <KeyboardArrowUpIcon />
    </Fab>
  )
}
